<template>
  <v-dialog v-model="modal" scrollable>
    <v-card>
      <v-card-title>
        Sélection de personne physique
      </v-card-title>
      <v-card-subtitle>{{ subtitle }}</v-card-subtitle>
      <v-card-text>
        <v-row>
          <v-col cols="12" sm="4">
            <search-by
              :datasource="datasource"
              attr="nom"
              i18n="recherche.rechercheSimple.personnePhysique.search.nom"
            />
          </v-col>
          <v-col cols="12" sm="4">
            <search-by
              :datasource="datasource"
              attr="prenom"
              i18n="recherche.rechercheSimple.personnePhysique.search.prenoms"
            />
          </v-col>
          <v-col cols="12" sm="4">
            <search-by
              :datasource="datasource"
              attr="raisonSociale"
              i18n="recherche.rechercheSimple.personneMorale.search.raisonSociale"
            />
          </v-col>
        </v-row>
        <datasource-table dense style="height:600px;" :datasource="datasource">
          <template #item.nom="{item}">
            {{ item.nom }}
          </template>
          <template #item.prenoms="{item}">
            {{ item.prenoms }}
          </template>
          <template #item.raisonSociale="{item}">
            {{ item.raisonSociale }}
          </template>
          <template v-if="mode == 'participant'" #item.typeParticipant="{item}">
            <v-select
              return-object
              v-model="item.typeParticipant"
              dense
              :items="enums.typesParticipant"
              :disabled="dejaParticipant(item)"
              item-text="libelle"
            >
              {{ item.libelle }}
            </v-select>
          </template>
          <template #item.referent="{item}">
            <v-tooltip v-if="mode == 'participant'" bottom>
              <template #activator="data">
                <v-checkbox
                  :disabled="dejaReferent(item)"
                  v-on="data.on"
                  :label="
                    $t('instance.detail.form.panel.participants.checkReferent')
                  "
                  v-model="item.referent"
                />
              </template>
              <span>{{ $t('instance.detail.form.referent.detail') }}</span>
            </v-tooltip>
          </template>
          <template #item.add="{item}">
            <v-tooltip v-if="mode == 'participant'" bottom>
              <template #activator="data">
                <v-btn
                  :disabled="dejaParticipant(item)"
                  v-on="data.on"
                  small
                  icon
                  @click="selectParticipant(item)"
                  color="primary"
                >
                  <v-icon>mdi-plus</v-icon>
                </v-btn>
              </template>
              <span>{{
                $t('instance.detail.form.panel.participants.addParticipant')
              }}</span>
            </v-tooltip>
            <v-tooltip v-if="mode == 'gestionnaire'" bottom>
              <template #activator="data">
                <v-btn
                  :disabled="dejaGestionnaire(item)"
                  v-on="data.on"
                  small
                  icon
                  @click="selectGestionnaire(item)"
                  color="primary"
                >
                  <v-icon>mdi-plus</v-icon>
                </v-btn>
              </template>
              <span>{{
                $t('instance.detail.form.panel.participants.addGestionnaire')
              }}</span>
            </v-tooltip>
          </template>
        </datasource-table>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn @click="closeModal" text>
          {{ $t('button.close') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import Datasource from '@/utils/datasource';
import toasted from '@/utils/toasted';

export default {
  props: {
    participants: {
      type: Array,
      daefault: [],
    },
    gestionnaires: {
      type: Array,
      daefault: [],
    },
    referent: {
      type: Number,
    },
    mode: {
      type: String,
      default: 'participant',
    },
  },
  data() {
    const dsParticipant = new Datasource('instance/recherche/simple/contact', {
      page: 1,
      rowsPerPage: 10,
      sortBy: ['nom', 'prenoms'],
      descending: [false],
      labelNamespace: 'recherche.rechercheSimple.personnePhysique.list',
      args: {
        actif: true,
      },
      fields: [
        {
          value: 'nom',
          sortable: true,
        },
        {
          value: 'prenoms',
          sortable: true,
        },
        {
          value: 'raisonSociale',
          sortable: false,
        },
        {
          value: 'typeParticipant',
          sortable: false,
        },
        {
          value: 'referent',
          text: this.$t('instance.detail.form.panel.participants.referent'),
          sortable: false,
        },
        {
          value: 'add',
          text: this.$t('instance.detail.form.panel.participants.add'),
          sortable: false,
        },
      ],
    });
    const dsGestionnaire = new Datasource('instance/gestionnaires', {
      page: 1,
      rowsPerPage: 15,
      sortBy: ['nom', 'prenoms'],
      descending: [false],
      labelNamespace: 'recherche.rechercheSimple.personnePhysique.list',
      args: {
        actif: true,
      },
      fields: [
        {
          value: 'nom',
          sortable: true,
        },
        {
          value: 'prenoms',
          sortable: true,
        },
        {
          value: 'raisonSociale',
          sortable: false,
        },
        {
          value: 'add',
          text: this.$t('instance.detail.form.panel.participants.add'),
          sortable: false,
        },
      ],
    });
    return {
      dsParticipant,
      dsGestionnaire,
      modal: false,
      type: null,
      enums: {
        typesParticipant: [],
      },
      innerParticipants: [],
      innerGestionnaires: [],
    };
  },
  computed: {
    datasource() {
      return this.mode === 'gestionnaire'
        ? this.dsGestionnaire
        : this.dsParticipant;
    },
    subtitle() {
      return (
        'Sélectionner une personne physique pour rajouter un nouveau ' +
        (this.mode === 'gestionnaire' ? 'gestionnaire' : 'participant') +
        '.'
      );
    },
  },
  watch: {
    participants(newList) {
      if (Array.isArray(newList) && newList.length > 0) {
        this.innerParticipants = newList.map(p => {
          return p.contact.id;
        });
      } else {
        this.innerParticipants = [];
      }
    },
    gestionnaires(newList) {
      if (Array.isArray(newList) && newList.length > 0) {
        this.innerGestionnaires = newList.map(c => {
          return c.id;
        });
      } else {
        this.internalGestionnaires = [];
      }
    },
  },
  methods: {
    dejaParticipant(contact) {
      return this.innerParticipants.includes(contact.id);
    },
    dejaGestionnaire(contact) {
      return this.innerGestionnaires.includes(contact.idPersonnePhysique);
    },
    dejaReferent(contact) {
      if (this.dejaParticipant(contact)) {
        return true;
      }
      if (contact.referent) {
        return false;
      }
      if (this.referent) {
        return true;
      }
      return this.dsParticipant.items.some(p => p.referent === true);
    },
    show() {
      this.modal = true;
      this.$api('enums/instance')
        .get()
        .then(response => {
          const body = response.body;
          const enums = this.enums;
          enums.typesParticipant = body.typesParticipant;
        });
    },
    selectParticipant(item) {
      if (item.typeParticipant) {
        const valeur = {
          id: item.id,
          nom: item.nom,
          prenoms: item.prenoms,
          idPersonnePhysique: item.idPersonnePhysique,
          idPersonneMorale: item.idPersonneMorale,
          raisonSociale: item.raisonSociale,
          fonction: item.fonction,
          type: item.typeParticipant.libelle,
          typeId: item.typeParticipant.id,
          referent: item.referent,
        };
        item.referent = false; // Après l'ajout, on décoche
        this.$emit('inputParticipant', valeur);
      } else {
        toasted.error('instance.ajouterParticipant.roleManquant');
      }
    },
    selectGestionnaire(item) {
      const valeur = {
        id: item.idPersonnePhysique,
        nom: item.nom,
        prenoms: item.prenoms,
      };

      this.$emit('inputGestionnaire', valeur);
    },

    closeModal() {
      this.modal = false;
    },
  },
};
</script>
